/**
 * Tenant-facing Argus Models hook (Wave 3 PR-A rebuild).
 *
 * Wraps GET /api/v1/argus-models/available — returns the brands the
 * sysadmin has assigned to this tenant in the I-M6.7 redacted shape,
 * now with capability flags + runtime state for the richer Studio
 * Models tab.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';

export type ModelRuntimeState = 'warm' | 'cold' | 'unknown';

export interface TenantBrandCapabilities {
  vision: boolean;
  tools: boolean;
  lora: boolean;
  longContext: boolean;
}

export interface TenantBrand {
  brandId: string;
  displayName: string;
  description: string;
  modality: string;
  capabilities: TenantBrandCapabilities;
  contextWindowK: number | null;
  runtimeState: ModelRuntimeState;
  assignedRoleLabel: string | null;
}

export interface TenantBrandsResponse {
  items: TenantBrand[];
}

export function useTenantArgusModels() {
  return useQuery<TenantBrandsResponse, Error>({
    queryKey: ['tenant-argus-models'],
    queryFn: () => apiClient.get<TenantBrandsResponse>('/argus-models/available'),
    // Runtime state can flip between calls. 30s staleness is fine —
    // the catalog itself rarely changes; warm/cold flips are operator-
    // visible elsewhere.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}
